import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from 'framer-motion';
import { useCaseStudyChapter } from '@/lib/caseStudyChapter';

/**
 * ReadingProgress — a slim lime scroll indicator pinned to the top of the
 * viewport (just below the main nav) so long case-study pages give readers
 * a clear sense of how far they've read and how much remains.
 *
 * Visual: 2px lime fill that grows left→right with scroll depth.
 * Chip:   floats top-right and shows either just the % (plain pages) OR the
 *         current chapter number/label followed by the % (rich case studies
 *         with tabs). The chapter label cross-fades whenever the reader
 *         switches phases.
 *
 * Purely visual — no layout impact (fixed positioning, ignores clicks).
 */
export const ReadingProgress = () => {
    const { scrollYProgress } = useScroll();
    const { chapter } = useCaseStudyChapter();

    // Smooth the raw scroll value so the fill doesn't judder on trackpads
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 140,
        damping: 24,
        mass: 0.3,
    });

    // Numeric percentage for the chip (0–100, integer)
    const [pct, setPct] = useState(0);
    useEffect(() => {
        const unsub = scrollYProgress.on('change', (v) => {
            setPct(Math.round(v * 100));
        });
        return () => unsub();
    }, [scrollYProgress]);

    // Hide the chip until the reader has actually started scrolling
    const chipOpacity = useTransform(scaleX, [0, 0.02, 1], [0, 1, 1]);

    return (
        <>
            {/* The bar itself — pinned to the very bottom edge of the viewport
                so it never fights the sticky nav at the top for attention. */}
            <motion.div
                aria-hidden
                data-testid="case-study-reading-progress"
                className="fixed left-0 right-0 bottom-0 h-[3px] bg-[var(--accent)] origin-left z-[45] pointer-events-none"
                style={{ scaleX }}
            />

            {/* Info chip — floats just above the bar on the right so the reader
                sees which chapter they are in and how far they've read without
                cluttering the top navigation. */}
            <motion.div
                aria-hidden
                data-testid="case-study-reading-progress-chip"
                style={{ opacity: chipOpacity }}
                className="fixed right-4 md:right-6 bottom-4 md:bottom-5 z-[45] pointer-events-none inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[var(--line-2)] bg-[var(--surface)]/90 backdrop-blur shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)]"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                <AnimatePresence mode="wait" initial={false}>
                    {chapter ? (
                        <motion.span
                            key={`ch-${chapter.index}`}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.25 }}
                            data-testid="case-study-reading-progress-chapter"
                            className="font-mono-brand text-[10px] tabular-nums uppercase tracking-[0.2em] text-[var(--ink)]"
                        >
                            {String(chapter.index + 1).padStart(2, '0')}
                            <span className="text-[var(--ink-3)] mx-1.5">·</span>
                            {chapter.label}
                        </motion.span>
                    ) : null}
                </AnimatePresence>
                {chapter && (
                    <span
                        aria-hidden
                        className="w-[1px] h-3 bg-[var(--line-2)]"
                    />
                )}
                <span className="font-mono-brand text-[10px] tabular-nums uppercase tracking-[0.2em] text-[var(--ink-2)]">
                    {String(pct).padStart(2, '0')}%
                </span>
            </motion.div>
        </>
    );
};

export default ReadingProgress;
